import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  MenuItem,
  IconButton,
  Stack,
  Divider,
  Fade,
  TextField,
  SelectChangeEvent,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { RootState } from "../../../store/Index";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Button from "../../../components/ui/Button";
import CustomTextField from "../../../components/ui/Textfield";
import PhoneNumberField from "../../../components/ui/PhoneNumberField";
import CustomSelect from "../../../components/ui/Select";


export interface DemoRequestModalProps {
  open: boolean;
  onClose: () => void;
  preSelectedTier?: string;
  /** Modal header title — e.g. "Book a Single Event", "Partner With Us", "Get a Demo" */
  title?: string;
  /** Submit button label — e.g. "Book Now", "Partner With Us", "Request My Demo" */
  submitLabel?: string;
}

const tiers = [
  { value: "cash", label: "Cash (Ad-hoc) — $699/stream" },
  { value: "tier1", label: "Tier 1: Base — $60,000/annum" },
  { value: "tier2", label: "Tier 2: Premium — $100,000/annum" },
  { value: "unsure", label: "Not sure yet" },
];

const DemoRequestModal: React.FC<DemoRequestModalProps> = ({
  open,
  onClose,
  preSelectedTier,
  title = "Request a Demo",
  submitLabel = "Request My Demo",
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    tier: preSelectedTier || "",
    message: "",
  });

  React.useEffect(() => {
    if (preSelectedTier !== undefined) {
      setForm((prev) => ({ ...prev, tier: preSelectedTier }));
    }
  }, [preSelectedTier]);

  const handlePhoneChange = (value: string) => {
    setForm((prev) => ({ ...prev, phone: value }));
  };

  const handleTierChange = (e: SelectChangeEvent<unknown>) => {
    setForm((prev) => ({ ...prev, tier: e.target.value as string }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    // TODO: Replace with actual API call / form submission
    await new Promise((res) => setTimeout(res, 1200));
    setLoading(false);
    setSubmitted(true);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSubmitted(false);
      setForm({
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        phone: "",
        tier: preSelectedTier || "",
        message: "",
      });
    }, 300);
  };

  const isFormValid =
    form.firstName && form.lastName && form.company && form.phone && form.tier;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          bgcolor: colors.primary[900],
          borderRadius: 3,
          boxShadow: `0 32px 80px rgba(0,0,0,0.6)`,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 4,
          pt: 4,
          pb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "1.5rem",
              fontWeight: 800,
              color: colors.primary[100],
              fontFamily: theme.typography.fontFamily,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: colors.primary[400],
              mt: 0.5,
            }}
          >
            Our team will be in touch.
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ color: colors.primary[400], mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: colors.primary[800] }} />

      <DialogContent sx={{ px: 4, py: 3 }}>
        {submitted ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CheckCircleIcon
              sx={{ fontSize: 56, color: colors.primary[400] }}
            />
            <Typography
              sx={{
                fontSize: "1.25rem",
                fontWeight: 700,
                color: colors.primary[100],
              }}
            >
              {"We've received your request!"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: colors.primary[300],
                maxWidth: 360,
              }}
            >
              A Muzukuru partnerships specialist will reach out to{" "}
              <strong style={{ color: colors.primary[100] }}>
                {form.email || form.phone}
              </strong>{" "}
              shortly.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {/* Name Row */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <CustomTextField
                fullWidth
                label="First Name*"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                size="small"
              />
              <CustomTextField
                fullWidth
                label="Last Name*"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                size="small"
              />
            </Stack>

            {/* Company */}
            <CustomTextField
              fullWidth
              label="Company*"
              name="company"
              value={form.company}
              onChange={handleChange}
              size="small"
            />

            {/* Contact Row */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <CustomTextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                size="small"
              />
              <PhoneNumberField
                fullWidth
                label="WhatsApp Number*"
                value={form.phone}
                onChange={handlePhoneChange}
                size="small"
              />
            </Stack>

            {/* Tier Select */}
            <CustomSelect
              fullWidth
              label="Interested Tier*"
              name="tier"
              value={form.tier}
              onChange={handleTierChange}
              size="small"
            >
              {tiers.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </CustomSelect>

            {/* Message */}
            <TextField
              fullWidth
              label="Anything else you'd like us to know? (optional)"
              name="message"
              value={form.message}
              onChange={handleChange}
              multiline
              rows={3}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: colors.primary[800],
                  borderRadius: 2,
                  color: colors.primary[100],
                  "& fieldset": { borderColor: colors.primary[600] },
                  "&:hover fieldset": { borderColor: colors.primary[400] },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary[400],
                  },
                },
                "& .MuiInputLabel-root": {
                  color: colors.primary[400],
                  "&.Mui-focused": { color: colors.primary[400] },
                },
              }}
            />

            {/* Submit */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormValid || loading}
              sx={{
                py: 1.75,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: "0.9375rem",
                textTransform: "none",
                mt: 0.5,
              }}
            >
              {loading ? "Submitting..." : submitLabel}
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DemoRequestModal;
